/**
 * The languages DrBell can speak — docs/31-global-languages.md.
 *
 * India first, then the world (the owner's decision of 3 October 2026). The
 * first nine carry their translations in the checked-in catalogue. The rest
 * arrive as one file each, `packages/i18n/locales/<code>.json`, translated by
 * Claude agents and loaded into D1 as `machine` (see `locale-file.ts`).
 *
 * Being in this list makes a code VALID: a profile may hold it, a bundle can
 * be compiled for it, the console can review it. Whether people are OFFERED
 * it is a separate, admin-controlled switch stored in D1 (`locales.enabled`,
 * migration 0044). `isDefaultEnabled` is that switch's starting position.
 */

export interface LocaleDef {
  /** BCP-47, and also what `Intl` is given: `te-IN`, `pt-BR`, `zh-Hant`. */
  code: string;
  /** English name, for the admin panel and the picker's search. */
  name: string;
  /** The language's own name — this is what the picker shows. */
  nativeName: string;
  /** ISO 15924 script. */
  script: string;
  /**
   * Written right to left. Such a language stays disabled until the apps can
   * mirror their layout (separate work), and the console says "needs RTL".
   */
  rtl: boolean;
  /**
   * More leading than Latin, for scripts whose marks clip at Latin line
   * heights: Indic matras, Thai and Lao tone marks stacked above vowels,
   * Sinhala and Odia, Ethiopic, Arabic-script descenders, Vietnamese stacked
   * diacritics. A real rendering bug, not a nicety.
   */
  lineHeightMultiplier: number;
  fallbackLocale: string | null;
  sortOrder: number;
  /** Translated in the checked-in catalogue (the first nine), not from a file. */
  inCatalogue: boolean;
}

type Row = [code: string, name: string, nativeName: string, script: string, rtl: boolean, lineHeight: number, fallback?: string | null];

/** The original nine, translated in `catalogue.ts`. Order is the picker's. */
const INDIA: Row[] = [
  ['en-IN', 'English', 'English', 'Latn', false, 1.0, null],
  ['hi-IN', 'Hindi', 'हिन्दी', 'Deva', false, 1.35],
  ['te-IN', 'Telugu', 'తెలుగు', 'Telu', false, 1.4],
  ['ta-IN', 'Tamil', 'தமிழ்', 'Taml', false, 1.35],
  ['bn-IN', 'Bengali', 'বাংলা', 'Beng', false, 1.35],
  // A Marathi reader reads Devanagari: Hindi is far closer than English.
  ['mr-IN', 'Marathi', 'मराठी', 'Deva', false, 1.35, 'hi-IN'],
  ['kn-IN', 'Kannada', 'ಕನ್ನಡ', 'Knda', false, 1.4],
  ['ml-IN', 'Malayalam', 'മലയാളം', 'Mlym', false, 1.4],
  ['gu-IN', 'Gujarati', 'ગુજરાતી', 'Gujr', false, 1.35],
];

/** Translated from `locales/<code>.json`. Grouped by region, as the owner listed them. */
const WORLD: Row[] = [
  // South Asia, beyond the first nine
  ['pa-IN', 'Punjabi', 'ਪੰਜਾਬੀ', 'Guru', false, 1.35],
  ['or-IN', 'Odia', 'ଓଡ଼ିଆ', 'Orya', false, 1.4],
  ['ne', 'Nepali', 'नेपाली', 'Deva', false, 1.35],
  ['si', 'Sinhala', 'සිංහල', 'Sinh', false, 1.4],
  // Middle East
  ['ar', 'Arabic', 'العربية', 'Arab', true, 1.3],
  ['he', 'Hebrew', 'עברית', 'Hebr', true, 1.15],
  ['fa', 'Persian', 'فارسی', 'Arab', true, 1.3],
  ['ur', 'Urdu', 'اردو', 'Arab', true, 1.4],
  ['tr', 'Turkish', 'Türkçe', 'Latn', false, 1.0],
  // Europe
  ['el', 'Greek', 'Ελληνικά', 'Grek', false, 1.05],
  ['ru', 'Russian', 'Русский', 'Cyrl', false, 1.0],
  ['uk', 'Ukrainian', 'Українська', 'Cyrl', false, 1.0],
  ['es', 'Spanish', 'Español', 'Latn', false, 1.0],
  ['pt-BR', 'Portuguese (Brazil)', 'Português (Brasil)', 'Latn', false, 1.0],
  ['pt-PT', 'Portuguese (Portugal)', 'Português (Portugal)', 'Latn', false, 1.0],
  ['it', 'Italian', 'Italiano', 'Latn', false, 1.0],
  ['fr', 'French', 'Français', 'Latn', false, 1.0],
  ['de', 'German', 'Deutsch', 'Latn', false, 1.0],
  ['nl', 'Dutch', 'Nederlands', 'Latn', false, 1.0],
  ['ro', 'Romanian', 'Română', 'Latn', false, 1.0],
  ['pl', 'Polish', 'Polski', 'Latn', false, 1.0],
  ['cs', 'Czech', 'Čeština', 'Latn', false, 1.05],
  ['sk', 'Slovak', 'Slovenčina', 'Latn', false, 1.05],
  ['hu', 'Hungarian', 'Magyar', 'Latn', false, 1.05],
  ['sv', 'Swedish', 'Svenska', 'Latn', false, 1.0],
  ['da', 'Danish', 'Dansk', 'Latn', false, 1.0],
  ['nb', 'Norwegian Bokmål', 'Norsk bokmål', 'Latn', false, 1.0],
  ['fi', 'Finnish', 'Suomi', 'Latn', false, 1.0],
  ['bg', 'Bulgarian', 'Български', 'Cyrl', false, 1.0],
  ['hr', 'Croatian', 'Hrvatski', 'Latn', false, 1.05],
  // Africa
  ['sw', 'Swahili', 'Kiswahili', 'Latn', false, 1.0],
  ['am', 'Amharic', 'አማርኛ', 'Ethi', false, 1.3],
  // East and South-East Asia
  ['zh-Hans', 'Chinese (Simplified)', '简体中文', 'Hans', false, 1.15],
  ['zh-Hant', 'Chinese (Traditional)', '繁體中文', 'Hant', false, 1.15],
  ['ja', 'Japanese', '日本語', 'Jpan', false, 1.15],
  ['ko', 'Korean', '한국어', 'Kore', false, 1.1],
  ['vi', 'Vietnamese', 'Tiếng Việt', 'Latn', false, 1.2],
  ['th', 'Thai', 'ไทย', 'Thai', false, 1.4],
  ['id', 'Indonesian', 'Bahasa Indonesia', 'Latn', false, 1.0],
  ['ms', 'Malay', 'Bahasa Melayu', 'Latn', false, 1.0],
  ['fil', 'Filipino', 'Filipino', 'Latn', false, 1.0],
];

function define(rows: Row[], inCatalogue: boolean, offset: number): LocaleDef[] {
  return rows.map(([code, name, nativeName, script, rtl, lineHeightMultiplier, fallback], i) => ({
    code,
    name,
    nativeName,
    script,
    rtl,
    lineHeightMultiplier,
    fallbackLocale: fallback === undefined ? 'en-IN' : fallback,
    sortOrder: offset + i,
    inCatalogue,
  }));
}

export const LOCALES: readonly LocaleDef[] = [...define(INDIA, true, 1), ...define(WORLD, false, 20)];

export const DEFAULT_LOCALE = 'en-IN';

export const LOCALE_CODES = LOCALES.map((l) => l.code);

const BY_CODE = new Map(LOCALES.map((l) => [l.code, l]));
const BY_LOWER = new Map(LOCALES.map((l) => [l.code.toLowerCase(), l.code]));

export function localeDef(code: string): LocaleDef | undefined {
  return BY_CODE.get(code);
}

/** A code DrBell knows. Not the same as one it currently offers: see `isDefaultEnabled`. */
export function isSupportedLocale(code: string): boolean {
  return BY_CODE.has(code);
}

/**
 * Where an admin's Languages switch starts, before anybody touches it.
 *
 * On: the nine with in-catalogue translations. Off: every right-to-left
 * language, whatever its file says, until the apps can mirror their layout.
 * Off for the rest too — until their file has been loaded and validated, at
 * which point the loader turns them on (`fileLoaded`).
 */
export function isDefaultEnabled(code: string, fileLoaded = false): boolean {
  const def = BY_CODE.get(code);
  if (!def) return false;
  if (def.inCatalogue) return true;
  if (def.rtl) return false;
  return fileLoaded;
}

/** The languages enabled before D1 has been asked — the nine. */
export const DEFAULT_ENABLED_CODES: readonly string[] = LOCALES.filter((l) => isDefaultEnabled(l.code)).map((l) => l.code);

/**
 * Device tags that name one of ours by another spelling. Language subtag →
 * our code, for languages where the bare subtag is not itself our code.
 */
const LANGUAGE_ALIASES: Record<string, string> = {
  pt: 'pt-BR', // the larger readership; Portugal is matched by region below
  zh: 'zh-Hans',
  no: 'nb',
  nn: 'nb',
  tl: 'fil',
  iw: 'he', // Java's legacy code, still reported by older Android
  in: 'id', // likewise
  pa: 'pa-IN',
  or: 'or-IN',
};

/** Traditional-script regions, for a device that says `zh-TW` rather than `zh-Hant`. */
const TRADITIONAL_CHINESE_REGIONS = new Set(['tw', 'hk', 'mo']);

/**
 * Best known match for a device locale, or English.
 *
 * A phone reporting `te` or `te-US` gets Telugu; `zh-TW` and `zh-Hant-HK`
 * get Traditional Chinese, `pt-PT` European Portuguese, `pt-AO` Brazilian
 * (the larger written standard), `nn-NO` Bokmål; anything unknown English.
 *
 * `enabled`, when given, is the set the person may be offered: a match that is
 * switched off falls back to English rather than to a language nobody
 * checked, which is how a person whose saved language was disabled carries on.
 */
export function resolveLocale(
  requested: string | null | undefined,
  enabled?: Iterable<string> | null,
): string {
  const found = matchDeviceLocale(requested);
  if (!enabled) return found;
  const allowed = new Set(enabled);
  return allowed.has(found) ? found : DEFAULT_LOCALE;
}

function matchDeviceLocale(requested: string | null | undefined): string {
  if (!requested) return DEFAULT_LOCALE;
  const tag = requested.trim().replace(/_/g, '-');
  const exact = BY_LOWER.get(tag.toLowerCase());
  if (exact) return exact;

  const parts = tag.toLowerCase().split('-');
  const language = parts[0] ?? '';
  const rest = parts.slice(1);

  if (language === 'zh') {
    if (rest.includes('hant') || rest.some((p) => TRADITIONAL_CHINESE_REGIONS.has(p))) return 'zh-Hant';
    return 'zh-Hans';
  }
  if (language === 'pt' && rest.includes('pt')) return 'pt-PT';

  const alias = LANGUAGE_ALIASES[language];
  if (alias) return alias;
  if (BY_CODE.has(language)) return language;
  const sameLanguage = LOCALES.find((l) => l.code.split('-')[0]!.toLowerCase() === language);
  return sameLanguage?.code ?? DEFAULT_LOCALE;
}

/**
 * Match what a *person* typed to a locale, or nothing.
 *
 * Distinct from `resolveLocale`, and the difference is the point.
 * `resolveLocale` negotiates a device locale and always returns something,
 * because a phone reporting an unknown tag should still get a working app.
 * This is for a human filling in a column called "Language", where the
 * answer is "Telugu" or "తెలుగు" and a silent
 * fall back to English would give the wrong language to the one person who
 * chose this app because it spoke theirs.
 *
 * So it accepts the forms a person actually writes and returns null when it
 * does not recognise one — or when it is ambiguous ("pt", "Chinese"),
 * leaving the caller to say so.
 */
export function matchLocaleName(input: string): string | null {
  const raw = input.trim();
  if (raw === '') return null;
  const lower = raw.toLowerCase();

  for (const l of LOCALES) if (l.code.toLowerCase() === lower) return l.code;
  for (const l of LOCALES) if (l.name.toLowerCase() === lower) return l.code;
  for (const l of LOCALES) if (l.nativeName === raw || l.nativeName.toLowerCase() === lower) return l.code;
  // A bare subtag, only when exactly one locale has it.
  const bySubtag = LOCALES.filter((l) => l.code.split('-')[0]!.toLowerCase() === lower);
  return bySubtag.length === 1 ? bySubtag[0]!.code : null;
}

/**
 * The search a language picker runs: the native name, the English name and
 * the code, ignoring case and accents ("espanol" finds Español).
 */
export function localeMatchesSearch(
  locale: Pick<LocaleDef, 'code' | 'name' | 'nativeName'>,
  query: string,
): boolean {
  return localeSearchRank(locale, query) >= 0;
}

/**
 * How well a language matches a search, best first: 0 the code itself,
 * 1 a name starting with it, 2 a word in a name starting with it, 3 anywhere
 * in a field; -1 no match. So "es" puts Español before Portuguese.
 */
export function localeSearchRank(
  locale: Pick<LocaleDef, 'code' | 'name' | 'nativeName'>,
  query: string,
): number {
  const q = fold(query.trim());
  if (!q) return 0;
  const code = fold(locale.code);
  const names = [fold(locale.nativeName), fold(locale.name)];
  if (code === q || code.split('-')[0] === q) return 0;
  if (names.some((n) => n.startsWith(q)) || code.startsWith(q)) return 1;
  if (names.some((n) => n.split(/[\s()]+/).some((w) => w.startsWith(q)))) return 2;
  if ([...names, code].some((f) => f.includes(q))) return 3;
  return -1;
}

/**
 * A picker's list: the enabled languages, the one the device suggests first
 * (when it is enabled and is not English), then the rest in registry order,
 * narrowed by the search. `enabled` is what `GET /api/i18n/locales` returned,
 * or `DEFAULT_ENABLED_CODES` when it could not be asked.
 */
export function pickerLocales<T extends Pick<LocaleDef, 'code' | 'name' | 'nativeName'>>(
  enabled: readonly T[],
  opts: { deviceLocale?: string | null; query?: string } = {},
): { suggested: T | null; rest: T[] } {
  const device = opts.deviceLocale ? resolveLocale(opts.deviceLocale) : null;
  const order = (l: T) => BY_CODE.get(l.code)?.sortOrder ?? 999;
  const rank = (l: T) => localeSearchRank(l, opts.query ?? '');
  const matching = enabled
    .filter((l) => rank(l) >= 0)
    .sort((a, b) => rank(a) - rank(b) || order(a) - order(b));
  const suggested =
    device && device !== DEFAULT_LOCALE ? matching.find((l) => l.code === device) ?? null : null;
  return { suggested, rest: matching.filter((l) => l !== suggested) };
}

function fold(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Locale chain to try, most specific first, ending at English. */
export function fallbackChain(code: string): string[] {
  const chain: string[] = [];
  let current: string | null = isSupportedLocale(code) ? code : DEFAULT_LOCALE;
  const seen = new Set<string>();
  while (current && !seen.has(current)) {
    seen.add(current);
    chain.push(current);
    current = BY_CODE.get(current)?.fallbackLocale ?? null;
  }
  if (!chain.includes(DEFAULT_LOCALE)) chain.push(DEFAULT_LOCALE);
  return chain;
}
